import React from 'react';
import {
  BellOutlined,
  CrownOutlined,
  GiftOutlined,
  IdcardOutlined,
  PlayCircleOutlined,
  TagsOutlined,
  TeamOutlined
} from '@ant-design/icons';

import { useSimpleAudienceList } from 'api/audiences';
import { useMediaTagList } from 'api/mediaTags';
import { useFeatureFlags } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { ConfigStepPanel, TC } from '../configStudio/kit/ConfigPanel';
import { OptionGrid, OptionTile } from '../configStudio/kit/OptionTile';
import { KeyValueRows } from '../configStudio/kit/configFields';
import { LoyaltySelect, SelectField, TextField } from '../wizard/kit/fields';
import { Eyebrow, Form, Line, Words } from '../wizard/kit/layout';
import { CampaignPicker, RewardPicker } from '../wizard/kit/pickers';
import { ruleStepErrors } from './ruleAdapter';
import {
  chooseOutcome,
  IRuleStepProps,
  RULE_OUTCOME_KINDS,
  RULE_STEP_TOTAL,
  RuleOutcome,
  RuleOutcomeKind
} from './ruleModel';

const TR = `${TC}.rule`;
const TD = `${TC}.rule.outcomeDetails`;

const OUTCOME_ICONS: Record<RuleOutcomeKind, React.ReactNode> = {
  stamp: <TagsOutlined />,
  game: <PlayCircleOutlined />,
  reward: <GiftOutlined />,
  points: <CrownOutlined />,
  audience: <TeamOutlined />,
  customer: <IdcardOutlined />,
  push: <BellOutlined />
};

/** 45 Step 3/4: seven outcome tiles in two columns; a pick moves on (no Next drawn). */
export const RuleOutcomeTypeStep: React.FC<IRuleStepProps> = ({ state, update, back }) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <ConfigStepPanel
      title={t(`${TR}.title`)}
      step={3}
      total={RULE_STEP_TOTAL}
      question={t(`${TR}.outcome.question`)}
      onNext={null}
      onBack={back}
      testId="orbit-rule-step-outcome"
    >
      <OptionGrid $columns={2}>
        {RULE_OUTCOME_KINDS.map(kind => (
          <OptionTile
            key={kind}
            icon={OUTCOME_ICONS[kind]}
            label={t(`${TR}.outcome.${kind}`)}
            selected={state.outcome.kind === kind}
            onSelect={() => update(current => chooseOutcome(current, kind))}
            testId={`orbit-rule-outcome-${kind}`}
          />
        ))}
      </OptionGrid>
    </ConfigStepPanel>
  );
};

type Patch = (patch: Partial<RuleOutcome>) => void;

const AudienceSelect: React.FC<{ outcome: RuleOutcome; patch: Patch }> = ({ outcome, patch }) => {
  const { t } = useMultiLanguageTranslation();
  const { data, isLoading } = useSimpleAudienceList();
  return (
    <SelectField<number>
      label={t(`${TD}.customAudience`)}
      placeholder={t(`${TD}.customAudiencePlaceholder`)}
      width="xl"
      showSearch
      loading={isLoading}
      value={outcome.audience?.id ?? null}
      options={(data ?? []).map(audience => ({ value: audience.id, label: audience.name }))}
      onChange={id => {
        const audience = data?.find(item => item.id === id);
        if (audience !== undefined) {
          patch({ audience: { id: audience.id, name: audience.name } });
        }
      }}
    />
  );
};

/** 53: the media tag line only with the module's `media_tag` flag (`OutcomeSection`). */
const PushFields: React.FC<{ outcome: RuleOutcome; patch: Patch }> = ({ outcome, patch }) => {
  const { t } = useMultiLanguageTranslation();
  const { media_tag: mediaTagOn } = useFeatureFlags() as Record<string, boolean | undefined>;
  const { data, isLoading } = useMediaTagList();
  return (
    <>
      {mediaTagOn ? (
        <Line>
          <Words>{t(`${TD}.mediaTag`)}</Words>
          <SelectField<number>
            label={t(`${TD}.mediaTag`)}
            placeholder={t(`${TC}.none`)}
            width="md"
            loading={isLoading}
            value={outcome.mediaTag}
            options={(data ?? []).map(tag => ({
              value: tag.id,
              label: tag.ratio_description ?? String(tag.id)
            }))}
            onChange={mediaTag => patch({ mediaTag })}
          />
        </Line>
      ) : null}
      <Eyebrow>{t(`${TD}.customPayload`)}</Eyebrow>
      <KeyValueRows
        rows={outcome.payload}
        keyLabel={t(`${TD}.payloadKey`)}
        valueLabel={t(`${TD}.payloadValue`)}
        addLabel={t(`${TD}.addCustomField`)}
        onChange={payload => patch({ payload })}
      />
    </>
  );
};

const OutcomeFields: React.FC<{ outcome: RuleOutcome; patch: Patch }> = ({ outcome, patch }) => {
  const { t } = useMultiLanguageTranslation();
  switch (outcome.kind) {
    case 'stamp':
    case 'game':
      return (
        <>
          <Line>
            <Words>{t(`${TD}.selectCampaign`)}</Words>
            <CampaignPicker
              label={t(`${TD}.selectCampaignButton`)}
              value={outcome.campaign}
              onChange={campaign => patch({ campaign })}
            />
          </Line>
          <Line>
            <Words>{t(`${TD}.${outcome.kind === 'stamp' ? 'stamps' : 'tries'}`)}</Words>
            <TextField
              label={t(`${TD}.${outcome.kind === 'stamp' ? 'stamps' : 'tries'}`)}
              placeholder="0"
              inputMode="numeric"
              value={outcome.quantity}
              onChange={quantity => patch({ quantity })}
            />
          </Line>
        </>
      );
    case 'reward':
      return (
        <Line>
          <Words>{t(`${TD}.selectReward`)}</Words>
          <RewardPicker
            label={t(`${TD}.selectRewardButton`)}
            value={outcome.reward}
            onChange={reward => patch({ reward })}
          />
        </Line>
      );
    case 'points':
      return (
        <Line>
          <Words>{t(`${TD}.award`)}</Words>
          <TextField
            label={t(`${TD}.points`)}
            placeholder="0"
            inputMode="numeric"
            value={outcome.points}
            onChange={points => patch({ points })}
          />
          <Words>{t(`${TD}.pointsFrom`)}</Words>
          <LoyaltySelect value={outcome.loyalty} onChange={loyalty => patch({ loyalty })} />
        </Line>
      );
    case 'audience':
      return (
        <Line>
          <Words>{t(`${TD}.customAudience`)}</Words>
          <AudienceSelect outcome={outcome} patch={patch} />
        </Line>
      );
    case 'customer':
      return (
        <KeyValueRows
          rows={outcome.properties}
          keyLabel={t(`${TD}.propertyKey`)}
          valueLabel={t(`${TD}.propertyValue`)}
          addLabel={t(`${TD}.addMore`)}
          onChange={properties => patch({ properties })}
        />
      );
    default:
      return <PushFields outcome={outcome} patch={patch} />;
  }
};

/** 48-53 Step 3/4: the picked outcome's details; "Issue a Reward" has no frame and follows 48. */
export const RuleOutcomeDetailsStep: React.FC<IRuleStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const patch: Patch = change =>
    update(current => ({ ...current, outcome: { ...current.outcome, ...change } }));
  return (
    <ConfigStepPanel
      title={t(`${TR}.title`)}
      step={3}
      total={RULE_STEP_TOTAL}
      question={t(`${TD}.${state.outcome.kind ?? 'push'}`)}
      errors={ruleStepErrors(state, t)}
      onNext={next}
      onBack={back}
      testId="orbit-rule-step-outcomeDetails"
    >
      <Form>
        <OutcomeFields outcome={state.outcome} patch={patch} />
      </Form>
    </ConfigStepPanel>
  );
};
